import React, { useContext, useEffect, useRef, useState } from "react";
import Widget from "./components/Widget";
import ChatGlobalContext, { ChatGlobalProvider } from "./context/ChatGlobalContext";
import Chat from "./Chat";
import { defaultMessages, initialTwilioChatState } from "./utils/chats";
import ChatWrapper from "./components/styled/ChatWrapper";
import { Message, chatLocalStorage, findUnreadedMessages, getAllMessages, isConversationActive } from "./services/chatService";
import './assets/styles/fonts.css';
import './assets/styles/app.scss'
import { getUrlParams } from "./utils/helperFn";

const TwilioChat3Inner = ({fullscreen = false, side = "right"}) => {
    const gContext: any = useContext(ChatGlobalContext);
    const messages = useRef<Message[]>([]);

    const [twilioChatUrlParams, setTwilioChatUrlParams] = useState<any>(null);
          
    useEffect(() => {    
        setTwilioChatUrlParams(getUrlParams());
        
        if(fullscreen && typeof document !== "undefined") document.getElementById("main_chat")?.remove();
        // Check if Conversation is closed in mean time
        const fetchConversationActive = async () => {
            try {
                const response:any = await isConversationActive();
                if(response === "CLOSED"){
                  gContext?.goResetTwilioChat();
                }
            } catch (error) {
              console.error("Error fetching is conversation active:", error);
            }
        };
        fetchConversationActive(); 

        // On reload get chat messages from previous session / on page reload
        const fetchExistingMessages = async (onlyMessages:boolean = false) => {
            try { 
                const tmpLocalStorage = await chatLocalStorage(); 
                if(chatLocalStorage('chatConversationId') && tmpLocalStorage){
                    const tmpMessages:any = await getAllMessages(); 
                    if(tmpMessages){
                        const tmpUnreadedMessages = await findUnreadedMessages(messages.current, tmpMessages);
                        if(tmpMessages && tmpLocalStorage && tmpUnreadedMessages){                          
                            const tmpState = 
                            onlyMessages ? 
                                {
                                    ...gContext?.goGetTwilioChat(),
                                    messages: tmpMessages,
                                    chatUnreaded: gContext?.goGetTwilioChat()?.chatConversationId && (!gContext?.goGetTwilioChat()?.openChat || !gContext?.goGetTwilioChat()?.visibilityChange) && Object.keys(tmpUnreadedMessages).length > 1,
                                    fullscreen: fullscreen,
                                    chatLoading: false,
                                    side: side
                                } : 
                                {
                                    ...initialTwilioChatState, 
                                    ...tmpLocalStorage || [],
                                    messages: tmpMessages,
                                    chatUnreaded: gContext?.goGetTwilioChat()?.chatConversationId && (!gContext?.goGetTwilioChat()?.openChat || !gContext?.goGetTwilioChat()?.visibilityChange) && Object.keys(tmpUnreadedMessages).length > 1,
                                    fullscreen: fullscreen,
                                    chatLoading: false,
                                    side: side
                                };
                            gContext?.goSetTwilioChat(tmpState);
                            messages.current = tmpMessages;
                            return tmpState;     
                        }
                    }
                } else {
                    const tmpState = {
                        ...initialTwilioChatState,
                        ...tmpLocalStorage || [],
                        chatUserType: null,
                        openChat: false,
                        fullscreen: fullscreen,
                        side: side
                    };  
                    gContext?.goSetTwilioChat(tmpState);
                    messages.current = defaultMessages;
                    return tmpState;   
                    // return {};
                }
            } catch (error) {
                console.error("Error fetching active conversation:", error);
            }
        };

        document.addEventListener('visibilitychange', async () => {
            if(gContext?.goGetTwilioChat()?.chatConversationId){
                const response:any = await fetchExistingMessages();
                if(response){
                    if (typeof document !== "undefined" && document.visibilityState === 'visible') {
                        gContext?.goSetTwilioChat({ 
                            ...gContext.twilioChat,
                            ...response,
                            visibilityChange: true,
                            chatUnreaded: false,
                        });
                        document.getElementById('inputMessage')?.focus();
                        localStorage.getItem('conversationId') && getAllMessages();
                    } else if (document.visibilityState === 'hidden') {
                        gContext?.goSetTwilioChat({ 
                            ...response,
                            visibilityChange: false
                        });
                    } else {
                        gContext?.goSetTwilioChat({ 
                            ...response,
                            visibilityChange: true
                        });
                    }
                }
            }
        });

        // Check for Existing Messages
        try {
            const waitForMessages = setTimeout(() => {
                const onLoadExistingMessages = async () => {
                    const response:any = await fetchExistingMessages();
                    if(response){
                        gContext?.goSetTwilioChat({ 
                            ...response,
                            chatLoading: false
                        });
                    }
                };
                onLoadExistingMessages(); 
            }, 2000);
            return () => clearTimeout(waitForMessages);
        } catch (error) {
            console.error("Error fetching existing messages:", error);
        }
    
        let lastUrl = typeof window !== 'undefined' ? window.location.href : '';

        const observer = new MutationObserver(() => {
            if (typeof window !== 'undefined' && lastUrl !== window.location.href) {
                lastUrl = window.location.href;
                setTwilioChatUrlParams(getUrlParams());
            }
        });

        typeof document !== 'undefined' && observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();

    }, []);

    useEffect(() => {
      if (typeof document !== "undefined") {
        if (fullscreen) {
          document.body.style.overflow = 'hidden';
          document.documentElement.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
          document.documentElement.style.overflow = '';
        }
      }
    }, [fullscreen])
  
    return (
        <ChatWrapper className={`chat-body ${fullscreen?'fullscreen':''} ${side || 'right'}`}>
            <Chat fullscreen={fullscreen} />
            { !fullscreen && <Widget /> }
        </ChatWrapper>
    );
};

const TwilioChat3 = (props) => (
  <ChatGlobalProvider>
    <TwilioChat3Inner {...props} />
  </ChatGlobalProvider>
);

export default TwilioChat3;
